import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Bulk import dialog for Aliança de Amor data (email; date; place per line).
export function AliancaImportDialog({ open, onClose, onDone }) {
  const [text, setText] = useState("");
  const [overwrite, setOverwrite] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  if (!open) return null;

  const submit = async () => {
    if (!text.trim()) { toast.error("Cole a lista antes de importar."); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/members/alianca-import`, { text, overwrite }, { withCredentials: true });
      setResult(r.data);
      if (r.data.counts.updated > 0) {
        toast.success(`${r.data.counts.updated} sócio(s) atualizado(s).`);
        onDone?.();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro na importação.");
    } finally {
      setBusy(false);
    }
  };

  const close = () => { setResult(null); setText(""); setOverwrite(false); onClose(); };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-6" data-testid="alianca-import-dialog">
      <div className="bg-white max-w-2xl w-full p-8 border border-[hsl(var(--border))] max-h-[90vh] overflow-y-auto">
        <div className="overline">Aliança de Amor</div>
        <h3 className="display-heading text-2xl mt-3">Importação em massa.</h3>
        <span className="accent-rule mt-5 block" />
        <p className="mt-5 text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">
          Uma linha por sócio, no formato <code className="bg-[hsl(var(--secondary))] px-1.5 py-0.5 text-xs">email; data; local</code>.
          A data aceita <strong>DD/MM/AAAA</strong> ou <strong>AAAA-MM-DD</strong>; o local é opcional.
        </p>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={7}
          placeholder={'exemplo@email.pt; 29/06/2013; Santuário "Cenáculo da Família do Pai" - Restelo'}
          className="mt-4 w-full bg-[hsl(var(--secondary))] border border-[hsl(var(--border))] p-3 text-xs font-mono focus:outline-none focus:border-[hsl(var(--accent))]"
          data-testid="alianca-import-textarea"
        />
        <label className="mt-3 flex items-center gap-2 text-sm cursor-pointer select-none">
          <input
            type="checkbox"
            checked={overwrite}
            onChange={(e) => setOverwrite(e.target.checked)}
            data-testid="alianca-import-overwrite"
          />
          Substituir dados já existentes (por omissão, sócios com Aliança registada são ignorados)
        </label>

        {result && (
          <div className="mt-5 border border-[hsl(var(--border))] bg-[hsl(var(--secondary))] p-4 text-sm space-y-2" data-testid="alianca-import-result">
            <div className="font-medium">
              Resultado: {result.counts.updated} atualizado(s) · {result.counts.skipped} ignorado(s) · {result.counts.not_found} não encontrado(s) · {result.counts.invalid} inválido(s)
            </div>
            {result.updated.length > 0 && (
              <ul className="text-xs text-emerald-800 list-disc pl-5">
                {result.updated.map((u) => <li key={u.email}>{u.name || u.email} — {u.date}{u.place ? ` · ${u.place}` : ""}</li>)}
              </ul>
            )}
            {result.skipped.length > 0 && (
              <ul className="text-xs text-amber-800 list-disc pl-5">
                {result.skipped.map((s) => <li key={s.email}>{s.name || s.email} — já tinha data ({s.existing_date}); use "Substituir" para forçar</li>)}
              </ul>
            )}
            {result.not_found.length > 0 && (
              <div className="text-xs text-red-700">Não encontrados: {result.not_found.join(", ")}</div>
            )}
            {result.invalid.length > 0 && (
              <ul className="text-xs text-red-700 list-disc pl-5">
                {result.invalid.map((i) => <li key={i.line}>Linha {i.line}: {i.reason}</li>)}
              </ul>
            )}
          </div>
        )}

        <div className="mt-6 flex flex-wrap gap-2">
          <button onClick={submit} disabled={busy} className="btn-primary disabled:opacity-60" data-testid="alianca-import-submit">
            <Upload className="h-4 w-4" /> {busy ? "A importar…" : "Importar"}
          </button>
          <button onClick={close} className="ml-auto text-xs uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]" data-testid="alianca-import-close">
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}
